package songwritersnotebook.app.components

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import songwritersnotebook.app.viewmodels.StepInterval
import songwritersnotebook.app.viewmodels.StudioSettings

@Composable
fun StudioSettingsDialog(
    settings: StudioSettings,
    isMetronomeOn: Boolean,
    isMetronomeMuted: Boolean,
    isTempoPreviewActive: Boolean,
    currentMetronomeBeat: Int,
    isSnappingOn: Boolean,
    snapInterval: StepInterval,
    onDismiss: () -> Unit,
    onBpmChange: (Int) -> Unit,
    onTimeSignatureChange: (Int, Int) -> Unit,
    onMetronomeToggle: (Boolean) -> Unit,
    onMetronomeMuteToggle: () -> Unit,
    onTempoPreviewToggle: () -> Unit,
    onSnappingToggle: (Boolean) -> Unit,
    onSnapIntervalChange: (StepInterval) -> Unit
) {
    var tempBpm by remember(settings.bpm) { mutableStateOf(settings.bpm) }
    var localBeats by remember { mutableStateOf(settings.timeSignature.beats.toString()) }
    var localPer by remember { mutableStateOf(settings.timeSignature.per.toString()) }

    var localSnapNumerator by remember { mutableStateOf(snapInterval.numerator.toString()) }
    var localSnapDenominator by remember { mutableStateOf(snapInterval.denominator.toString()) }

    fun safeToInt(s: String, fallback: Int): Int {
        return s.filter { it.isDigit() }.toIntOrNull() ?: fallback
    }

    val commitAndDismiss = {
        onBpmChange(tempBpm)
        val beats = localBeats.trim().toIntOrNull()
        val per = localPer.trim().toIntOrNull()
        if (beats != null && per != null && beats > 0 && per > 0) {
            onTimeSignatureChange(beats, per)
        }
        onDismiss()
    }

    AlertDialog(
        onDismissRequest = commitAndDismiss,
        title = { Text("Studio Settings", fontWeight = FontWeight.Bold) },
        text = {
            Column(verticalArrangement = Arrangement.spacedBy(16.dp)) {
                // BPM Slider & Tempo Preview
                MetronomeSettingItem(
                    label = "Tempo",
                    bpm = tempBpm,
                    onBpmChange = { tempBpm = it },
                    isPreviewActive = isTempoPreviewActive,
                    onPreviewToggle = { onTempoPreviewToggle() },
                    isMuted = isMetronomeMuted,
                    onMutedChange = { onMetronomeMuteToggle() },
                    currentBeat = currentMetronomeBeat,
                    showDivider = true
                )

                // Metronome Global Toggle
                Row(
                    modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.SpaceBetween
                ) {
                    Text("Metronome", style = MaterialTheme.typography.bodyLarge)
                    Switch(checked = isMetronomeOn, onCheckedChange = onMetronomeToggle)
                }

                // Time Signature
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.SpaceBetween
                ) {
                    Text("Time Signature", style = MaterialTheme.typography.bodyLarge)
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        OutlinedTextField(
                            value = localBeats,
                            onValueChange = { 
                                if (it.isEmpty() || it.all { char -> char.isDigit() }) {
                                    localBeats = it
                                    val beats = safeToInt(it, 4)
                                    val per = safeToInt(localPer, 4)
                                    if (beats > 0) onTimeSignatureChange(beats, per)
                                }
                            },
                            modifier = Modifier.width(60.dp),
                            textStyle = LocalTextStyle.current.copy(textAlign = androidx.compose.ui.text.style.TextAlign.Center),
                            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
                            singleLine = true
                        )
                        Text("/", modifier = Modifier.padding(horizontal = 8.dp))
                        OutlinedTextField(
                            value = localPer,
                            onValueChange = { 
                                if (it.isEmpty() || it.all { char -> char.isDigit() }) {
                                    localPer = it
                                    val per = safeToInt(it, 4)
                                    val beats = safeToInt(localBeats, 4)
                                    if (per > 0) onTimeSignatureChange(beats, per)
                                }
                            },
                            modifier = Modifier.width(60.dp),
                            textStyle = LocalTextStyle.current.copy(textAlign = androidx.compose.ui.text.style.TextAlign.Center),
                            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
                            singleLine = true
                        )
                    }
                }

                // Snapping
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.SpaceBetween
                ) {
                    Text("Snapping", style = MaterialTheme.typography.bodyLarge)
                    Switch(checked = isSnappingOn, onCheckedChange = onSnappingToggle)
                }

                AnimatedVisibility(visible = isSnappingOn) {
                    Column(
                        modifier = Modifier.padding(top = 12.dp, start = 8.dp, end = 8.dp),
                        verticalArrangement = Arrangement.spacedBy(12.dp)
                    ) {
                        // Segmented Button Replacement
                        Row(modifier = Modifier.fillMaxWidth()) {
                            val isBar = snapInterval.base == "bar"
                            OutlinedButton(
                                onClick = { onSnapIntervalChange(snapInterval.copy(base = "bar")) },
                                modifier = Modifier.weight(1f),
                                colors = if (isBar) ButtonDefaults.buttonColors() else ButtonDefaults.outlinedButtonColors()
                            ) { Text("Bar") }
                            Spacer(Modifier.width(8.dp))
                            OutlinedButton(
                                onClick = { onSnapIntervalChange(snapInterval.copy(base = "beat")) },
                                modifier = Modifier.weight(1f),
                                colors = if (!isBar) ButtonDefaults.buttonColors() else ButtonDefaults.outlinedButtonColors()
                            ) { Text("Beat") }
                        }

                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.Center
                        ) {
                            OutlinedTextField(
                                value = localSnapNumerator,
                                onValueChange = { 
                                    localSnapNumerator = it 
                                    val num = safeToInt(it, 1)
                                    onSnapIntervalChange(snapInterval.copy(numerator = num))
                                },
                                modifier = Modifier.width(70.dp),
                                textStyle = LocalTextStyle.current.copy(textAlign = androidx.compose.ui.text.style.TextAlign.Center),
                                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
                                singleLine = true
                            )
                            Text("/", modifier = Modifier.padding(horizontal = 8.dp), style = MaterialTheme.typography.titleLarge)
                            OutlinedTextField(
                                value = localSnapDenominator,
                                onValueChange = { 
                                    localSnapDenominator = it 
                                    val den = safeToInt(it, 4)
                                    if (den > 0) onSnapIntervalChange(snapInterval.copy(denominator = den))
                                },
                                modifier = Modifier.width(70.dp),
                                textStyle = LocalTextStyle.current.copy(textAlign = androidx.compose.ui.text.style.TextAlign.Center),
                                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
                                singleLine = true
                            )
                        }
                    }
                }
            }
        },
        dismissButton = {
            TextButton(onClick = onDismiss) {
                Text("Cancel")
            }
        },
        confirmButton = {
            TextButton(onClick = commitAndDismiss) {
                Text("Done")
            }
        }
    )
}
